/**
 * Demo Configuration Module
 * Centralizes all demo-related configuration to eliminate hardcoded values
 */

interface DemoConfig {
  enabled: boolean;
  restaurantId: number | null;
  restaurantName: string;
  restAccountId: number | null;
  locationId: number | null;
  imageBasePath: string;
}

/**
 * Get demo configuration from environment variables
 */
const getDemoConfig = (): DemoConfig => {
  // Check if demo mode is enabled (default true for backward compatibility)
  const enabled = import.meta.env.VITE_DEMO_MODE !== 'false';
  
  // Get restaurant ID (default 60 for backward compatibility)
  const restaurantId = enabled 
    ? parseInt(import.meta.env.VITE_DEMO_RESTAURANT_ID || '60') 
    : null;
  
  // Get account ID (default 19 for backward compatibility)
  const restAccountId = enabled 
    ? parseInt(import.meta.env.VITE_DEMO_REST_ACCOUNT_ID || '19')
    : null;
  
  // Get location ID (default 1 for backward compatibility)
  const locationId = enabled
    ? parseInt(import.meta.env.VITE_DEMO_LOCATION_ID || '1')
    : null;
  
  return {
    enabled,
    restaurantId,
    restaurantName: import.meta.env.VITE_DEMO_RESTAURANT_NAME || 'The Flavor Lab',
    restAccountId,
    locationId,
    imageBasePath: restaurantId 
      ? `/api/images/static/${restaurantId}/menu/`
      : '/api/images/'
  };
};

// Export singleton configuration
export const DEMO_CONFIG = getDemoConfig();

// Helper functions for easy access
export const isDemoMode = (): boolean => DEMO_CONFIG.enabled;
export const getDemoRestaurantId = (): number | null => DEMO_CONFIG.restaurantId;
export const getDemoRestaurantName = (): string => DEMO_CONFIG.restaurantName;
export const getDemoRestAccountId = (): number | null => DEMO_CONFIG.restAccountId;
export const getDemoLocationId = (): number | null => DEMO_CONFIG.locationId;

/**
 * Get demo image URL for a menu item
 */
export const getDemoImageUrl = (menuItemId: number, version = 'v3'): string => {
  return `${DEMO_CONFIG.imageBasePath}${menuItemId}/${version}.png`;
};

/**
 * Check if current context is demo
 */
export const isDemoContext = (restaurantId?: number): boolean => {
  return isDemoMode() && restaurantId === DEMO_CONFIG.restaurantId;
};

// Export demo restaurant data for success metrics
export const DEMO_RESTAURANTS = {
  flavorLab: {
    name: 'The Flavor Lab',
    location: 'Miami, FL',
    metric: '$4,200',
    period: 'annual revenue opportunity',
    highlight: 'From AI-discovered drink pairings',
    menuItemId: 4259, // Frozen Margarita
    imageVersion: 'v3'
  },
  grandTavern: {
    name: 'The Opulent Restaurant & Lounge',
    location: 'Wilmington, DE',
    metric: '15 hours',
    period: 'saved weekly',
    highlight: 'On customer inquiries',
    customImage: '/images/60/menu/4241/v2.png', // Grilled Mahi Mahi - elegant main course
    caseStudyLink: '/case-studies/the-opulent-virtual-assistant-success'
  },
  pizzaPalace: {
    name: "Ocean's Edge",
    location: 'Miami, FL',
    metric: '32%',
    period: 'increase in order value',
    highlight: 'Through smart upselling',
    menuItemId: 4238, // Coconut Shrimp
    imageVersion: 'v2'
  }
};

// Backward compatibility exports
export const DEMO_RESTAURANT_ID = DEMO_CONFIG.restaurantId;
export const DEMO_RESTAURANT_NAME = DEMO_CONFIG.restaurantName;